import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import API from '../services/api';
import '../css/Login.css';

// ============================================
// Particle System for Animated Background
// ============================================
class Particle {
 constructor(canvas) {
 this.canvas = canvas;
 this.reset();
 }

 reset() {
 this.x = Math.random() * this.canvas.width;
 this.y = Math.random() * this.canvas.height;
 this.size = Math.random() * 2 + 0.5;
 this.speedX = (Math.random() - 0.5) * 0.5;
 this.speedY = (Math.random() - 0.5) * 0.5;
 this.opacity = Math.random() * 0.5 + 0.1;
 this.fadeDirection = Math.random() > 0.5 ? 1 : -1;
 }

 update() {
 this.x += this.speedX;
 this.y += this.speedY;
 this.opacity += this.fadeDirection * 0.003;

 if (this.opacity <= 0.05 || this.opacity >= 0.6) {
 this.fadeDirection *= -1;
 }

 if (this.x < 0 || this.x > this.canvas.width ||
 this.y < 0 || this.y > this.canvas.height) {
 this.reset();
 }
 }

 draw(ctx) {
 ctx.beginPath();
 ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
 ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
 ctx.fill();
 }
}

// ============================================
// Vehicle SVG Component (3D Style)
// ============================================
const VehicleSVG = () => (
 <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
 {/* Car body */}
 <path d="M20 55 L25 40 L40 30 L80 30 L95 40 L100 55 Z"
 fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
 {/* Car roof */}
 <path d="M35 30 L42 18 L78 18 L85 30"
 fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />
 {/* Windows */}
 <path d="M40 30 L45 20 L57 20 L57 30 Z" fill="rgba(14,154,184,0.2)" stroke="rgba(14,154,184,0.4)" strokeWidth="0.8" />
 <path d="M63 30 L63 20 L75 20 L80 30 Z" fill="rgba(14,154,184,0.2)" stroke="rgba(14,154,184,0.4)" strokeWidth="0.8" />
 {/* Wheels */}
 <circle cx="35" cy="58" r="8" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
 <circle cx="35" cy="58" r="4" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
 <circle cx="85" cy="58" r="8" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
 <circle cx="85" cy="58" r="4" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
 {/* Headlights */}
 <ellipse cx="98" cy="48" rx="3" ry="4" fill="rgba(14,154,184,0.4)" />
 <ellipse cx="22" cy="48" rx="3" ry="4" fill="rgba(239,68,68,0.3)" />
 {/* Ground line */}
 <line x1="10" y1="66" x2="110" y2="66" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
 {/* Road reflection */}
 <path d="M20 70 L25 67 L95 67 L100 70 Z" fill="rgba(255,255,255,0.03)" />
 </svg>
);

// ============================================
// Password Strength Hook
// ============================================
const usePasswordStrength = (password) => {
 if (!password) return { level: 0, label: '', class: '' };

 let score = 0;
 if (password.length >= 6) score++;
 if (password.length >= 8) score++;
 if (/[A-Z]/.test(password)) score++;
 if (/[0-9]/.test(password)) score++;
 if (/[^A-Za-z0-9]/.test(password)) score++;

 if (score <= 1) return { level: 1, label: 'Yếu', class: 'strength-weak' };
 if (score <= 2) return { level: 2, label: 'Trung bình', class: 'strength-fair' };
 if (score <= 3) return { level: 3, label: 'Tốt', class: 'strength-good' };
 return { level: 4, label: 'Mạnh', class: 'strength-strong' };
};

// ============================================
// Login Component
// ============================================
const Login = () => {
 const [username, setUsername] = useState('');
 const [password, setPassword] = useState('');
 const [rememberMe, setRememberMe] = useState(false);
 const [isLoading, setIsLoading] = useState(false);
 const [error, setError] = useState('');
 const [showPassword, setShowPassword] = useState(false);
 const [currentYear] = useState(new Date().getFullYear());
 const [loginSuccess, setLoginSuccess] = useState(false);
 const [biometricLoading, setBiometricLoading] = useState(false);
 const [biometricAvailable, setBiometricAvailable] = useState(false);
 const [hasPasskey, setHasPasskey] = useState(false);
 const [showBiometricGuide, setShowBiometricGuide] = useState(false);
 const loginInFlightRef = useRef(false);
 const canvasRef = useRef(null);
 const animationRef = useRef(null);
 const navigate = useNavigate();

 const passwordStrength = usePasswordStrength(password);

 // ---- Particle Background ----
 const initParticles = useCallback(() => {
 const canvas = canvasRef.current;
 if (!canvas) return;

 const ctx = canvas.getContext('2d');
 let particles = [];

 const resize = () => {
 canvas.width = window.innerWidth;
 canvas.height = window.innerHeight;
 };
 resize();
 window.addEventListener('resize', resize);

 // Create particles
 const particleCount = Math.min(60, Math.floor(window.innerWidth / 20));
 for (let i = 0; i < particleCount; i++) {
 particles.push(new Particle(canvas));
 }

 // Draw connections between close particles
 const drawConnections = () => {
 for (let i = 0; i < particles.length; i++) {
 for (let j = i + 1; j < particles.length; j++) {
 const dx = particles[i].x - particles[j].x;
 const dy = particles[i].y - particles[j].y;
 const dist = Math.sqrt(dx * dx + dy * dy);

 if (dist < 150) {
 const opacity = (1 - dist / 150) * 0.15;
 ctx.beginPath();
 ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
 ctx.lineWidth = 0.5;
 ctx.moveTo(particles[i].x, particles[i].y);
 ctx.lineTo(particles[j].x, particles[j].y);
 ctx.stroke();
 }
 }
 }
 };

 // Animation loop
 const animate = () => {
 ctx.clearRect(0, 0, canvas.width, canvas.height);
 particles.forEach(p => {
 p.update();
 p.draw(ctx);
 });
 drawConnections();
 animationRef.current = requestAnimationFrame(animate);
 };
 animate();

 return () => {
 window.removeEventListener('resize', resize);
 if (animationRef.current) {
 cancelAnimationFrame(animationRef.current);
 }
 };
 }, []);

 useEffect(() => {
 const cleanup = initParticles();
 return cleanup;
 }, [initParticles]);

 // ---- Remember Me ----
 useEffect(() => {
 const savedUsername = localStorage.getItem('rememberedUsername');
 const savedPassword = localStorage.getItem('rememberedPassword');
 if (savedUsername && savedPassword) {
 setUsername(savedUsername);
 setPassword(savedPassword);
 setRememberMe(true);
 }
 }, []);

 // ---- Check WebAuthn availability ----
 useEffect(() => {
 const checkBiometric = async () => {
 try {
 if (window.PublicKeyCredential) {
 const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
 if (available) {
 setBiometricAvailable(true);
 return;
 }
 // On mobile PWA, platform check can return false even when passkeys work
 if (window.isSecureContext) {
 setBiometricAvailable(true);
 return;
 }
 }
 } catch {
 setBiometricAvailable(!!window.isSecureContext && !!window.PublicKeyCredential);
 }
 };
 checkBiometric();
 }, []);

 // ---- Check if username has passkey ----
 useEffect(() => {
 if (!username || username.length < 2) {
 setHasPasskey(false);
 return;
 }
 const timer = setTimeout(async () => {
 try {
 const { data } = await API.post('/auth/webauthn/check', { username: username.trim() });
 setHasPasskey(data.hasPasskey);
 } catch {
 setHasPasskey(false);
 }
 }, 500);
 return () => clearTimeout(timer);
 }, [username]);

 // ---- Biometric Login Handler ----
 const handleBiometricLogin = async () => {
 if (biometricLoading || loginSuccess) return;
 setBiometricLoading(true);
 setError('');

 try {
 // Get authentication options
 const optionsPayload = username.trim() ? { username: username.trim() } : {};
 const { data: options } = await API.post('/auth/webauthn/login-options', optionsPayload);

 const tempChallenge = options._tempChallenge;
 delete options._tempChallenge;

 // Trigger biometric prompt
 const authResponse = await startAuthentication({ optionsJSON: options });

 // Verify with server
 const { data: result } = await API.post('/auth/webauthn/login-verify', {
 ...authResponse,
 username: username.trim() || undefined,
 _tempChallenge: tempChallenge || undefined,
 });

 if (result.verified && result.user) {
 const user = { ...result.user, id: result.user.id || result.user._id };
 if (result.token) {
 localStorage.setItem('authToken', result.token);
 localStorage.setItem('token', result.token);
 }
 localStorage.setItem('user', JSON.stringify(user));
 window.dispatchEvent(new Event('authChange'));

 setLoginSuccess(true);
 setTimeout(() => {
 navigate('/', { replace: true });
 }, 800);
 }
 } catch (err) {
 console.error('Biometric login error:', err);
 if (err.name === 'NotAllowedError') {
 setError('Đã hủy xác thực sinh trắc học.');
 } else {
 setError(err.response?.data?.error || 'Đăng nhập sinh trắc học thất bại. Vui lòng thử lại.');
 }
 } finally {
 setBiometricLoading(false);
 }
 };

 // ---- Login Handler ----
 const handleLogin = async (e) => {
 e.preventDefault();

 if (loginInFlightRef.current || isLoading || loginSuccess) {
 return;
 }

 loginInFlightRef.current = true;
 setIsLoading(true);
 setError('');

 try {
 const response = await API.post(
 '/auth/login',
 { username: username.trim(), password },
 {
 headers: {
 'Content-Type': 'application/json'
 },
 retry: 1
 }
 );

 if (response.data.user) {
 const user = {
 ...response.data.user,
 id: response.data.user.id || response.data.user._id,
 };
 const authToken = response.data?.token;
 if (authToken) {
 localStorage.setItem('authToken', authToken);
 localStorage.setItem('token', authToken);
 } else {
 localStorage.removeItem('token');
 localStorage.removeItem('authToken');
 }
 localStorage.setItem('user', JSON.stringify(user));
 window.dispatchEvent(new Event('authChange'));

 if (rememberMe) {
 localStorage.setItem('rememberedUsername', username);
 localStorage.setItem('rememberedPassword', password);
 } else {
 localStorage.removeItem('rememberedUsername');
 localStorage.removeItem('rememberedPassword');
 }

 // Trigger success animation then redirect
 setLoginSuccess(true);
 setTimeout(() => {
 navigate('/', { replace: true });
 }, 800);
 }
 } catch (error) {
 console.error('Login error:', error);
 if (error.response?.status === 429) {
 setError(
 error.response?.data?.message ||
 'Bạn đăng nhập quá nhiều lần trong thời gian ngắn. Vui lòng đợi ít phút rồi thử lại.'
 );
 } else {
 setError(error.response?.data?.message || 'Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.');
 }
 } finally {
 loginInFlightRef.current = false;
 setIsLoading(false);
 }
 };

 return (
 <>
 {/* Animated Background */}
 <div className="login-animated-bg" />
 <div className="login-floating-orb login-floating-orb--1" />
 <div className="login-floating-orb login-floating-orb--2" />
 <div className="login-floating-orb login-floating-orb--3" />
 <canvas ref={canvasRef} className="login-particles-canvas" />

 <div className={`modern-login-container ${loginSuccess ? 'login-success' : ''}`}>
 <div className="modern-login-card">
 {/* Left Side - Branding & 3D Illustration */}
 <div className="modern-login-left">
 <div className="modern-login-branding">
 <div className="modern-login-logo">
 <div className="modern-login-logo-icon">
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
 </svg>
 </div>
 <h1 className="modern-login-company">NEW ATLANTIC</h1>
 </div>
 <h2 className="modern-login-welcome">Chào mừng trở lại</h2>
 <p className="modern-login-subtitle">Đăng nhập để bắt đầu sử dụng hệ thống quản lý</p>
 </div>

 {/* 3D Vehicle SVG Illustration */}
 <div className="modern-login-decoration">
 <div className="modern-login-vehicle-glow" />
 <div className="modern-login-deco-ring" />
 <div className="modern-login-deco-ring modern-login-deco-ring--2" />
 <div className="modern-login-vehicle-svg">
 <VehicleSVG />
 </div>
 </div>

 {/* Feature Badges */}
 <div className="modern-login-features">
 <span className="modern-login-feature-badge">
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
 </svg>
 Bảo mật cao
 </span>
 <span className="modern-login-feature-badge">
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
 </svg>
 Nhanh chóng
 </span>
 <span className="modern-login-feature-badge">
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
 </svg>
 Đa nền tảng
 </span>
 </div>

 <div className="modern-login-footer">
 © {currentYear} New Atlantic. Bản quyền thuộc về công ty.
 </div>
 </div>

 {/* Right Side - Login Form */}
 <div className="modern-login-right">
 <div className="modern-login-header">
 <h3 className="modern-login-title">Đăng nhập vào tài khoản</h3>
 <p className="modern-login-description">Nhập thông tin đăng nhập để truy cập hệ thống</p>
 </div>

 {error && (
 <div className="modern-login-error">
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
 <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
 </svg>
 <span>{error}</span>
 </div>
 )}

 <form onSubmit={handleLogin} className="modern-login-form">
 {/* Username */}
 <div className="modern-login-input-group">
 <label htmlFor="username" className="modern-login-label">Tài khoản</label>
 <div className="modern-login-input-container">
 <div className="modern-login-input-icon">
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
 </svg>
 </div>
 <input
 id="username"
 name="username"
 type="text"
 autoComplete="username"
 required
 value={username}
 onChange={(e) => setUsername(e.target.value)}
 onFocus={() => setError('')}
 className={`modern-login-input ${username.length >= 3 ? 'input-valid' : ''}`}
 placeholder="Nhập tên đăng nhập"
 disabled={isLoading || loginSuccess}
 />
 </div>
 </div>

 {/* Password */}
 <div className="modern-login-input-group">
 <label htmlFor="password" className="modern-login-label">Mật khẩu</label>
 <div className="modern-login-input-container">
 <div className="modern-login-input-icon">
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
 </svg>
 </div>
 <input
 id="password"
 name="password"
 type={showPassword ? "text" : "password"}
 autoComplete="current-password"
 required
 value={password}
 onChange={(e) => setPassword(e.target.value)}
 onFocus={() => setError('')}
 className="modern-login-input"
 placeholder="Nhập mật khẩu"
 disabled={isLoading || loginSuccess}
 />
 <div
 className="modern-login-password-toggle"
 onClick={() => !isLoading && setShowPassword(!showPassword)}
 role="button"
 aria-label={showPassword ? "Ẩn mật khẩu" : "Hiển thị mật khẩu"}
 tabIndex={0}
 onKeyPress={(e) => {
 if (e.key === 'Enter' || e.key === ' ') {
 !isLoading && setShowPassword(!showPassword);
 }
 }}
 >
 {showPassword ? (
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
 </svg>
 ) : (
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
 </svg>
 )}
 </div>
 </div>
 {/* Password Strength Indicator */}
 {password && (
 <>
 <div className="password-strength-bar">
 <div className={`password-strength-fill ${passwordStrength.class}`} />
 </div>
 <span className="password-strength-text">
 Độ mạnh: {passwordStrength.label}
 </span>
 </>
 )}
 </div>

 {/* Remember Me */}
 <div className="modern-login-options">
 <div className="modern-login-remember">
 <input
 id="rememberMe"
 name="rememberMe"
 type="checkbox"
 checked={rememberMe}
 onChange={(e) => setRememberMe(e.target.checked)}
 className="modern-login-checkbox"
 disabled={isLoading || loginSuccess}
 />
 <label htmlFor="rememberMe" className="modern-login-checkbox-label">
 Ghi nhớ đăng nhập
 </label>
 </div>
 </div>

 {/* Submit Button */}
 <div className="modern-login-submit">
 <button
 type="submit"
 disabled={isLoading || loginSuccess}
 className={`modern-login-button ${loginSuccess ? 'btn-success' : ''}`}
 >
 {loginSuccess ? (
 <>
 <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
 </svg>
 <span>Đăng nhập thành công!</span>
 </>
 ) : isLoading ? (
 <>
 <svg className="modern-login-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
 <circle className="modern-login-spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
 <path className="modern-login-spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 <span>Đang đăng nhập...</span>
 </>
 ) : (
 <span>Đăng nhập</span>
 )}
 </button>
 </div>
 </form>

 {/* Biometric Login */}
 {biometricAvailable && (
 <div className="modern-login-biometric">
 <div className="modern-login-divider">
 <span>hoặc</span>
 </div>
 <button
 type="button"
 onClick={handleBiometricLogin}
 disabled={biometricLoading || loginSuccess || (!hasPasskey && !username)}
 className={`modern-login-biometric-btn ${hasPasskey ? 'has-passkey' : ''}`}
 title={!hasPasskey && username ? 'Tài khoản chưa đăng ký vân tay' : 'Đăng nhập bằng vân tay / Face ID'}
 >
 {biometricLoading ? (
 <>
 <svg className="modern-login-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
 <circle className="modern-login-spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
 <path className="modern-login-spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 <span>Đang xác thực...</span>
 </>
 ) : (
 <>
 <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
 <path d="M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
 <path d="M12 4c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8"/>
 <path d="M12 2a10 10 0 0 1 10 10"/>
 <path d="M12 6a6 6 0 0 1 6 6"/>
 <path d="M12 8a4 4 0 0 1 4 4"/>
 </svg>
 <span>Đăng nhập bằng Vân tay / Face ID</span>
 </>
 )}
 </button>
 {hasPasskey && (
 <p className="modern-login-biometric-hint">
 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
 <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
 <polyline points="22 4 12 14.01 9 11.01"/>
 </svg>
 Đã đăng ký sinh trắc học
 </p>
 )}
 {!hasPasskey && (
 <div className="modern-login-biometric-setup-guide">
 <button
 type="button"
 className="modern-login-biometric-guide-toggle"
 onClick={() => setShowBiometricGuide(!showBiometricGuide)}
 >
 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
 <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
 </svg>
 <span>{showBiometricGuide ? 'Ẩn hướng dẫn' : 'Đăng nhập bằng Vân tay / Face ID là gì?'}</span>
 <svg className={`modern-login-guide-chevron ${showBiometricGuide ? 'open' : ''}`} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
 <polyline points="6 9 12 15 18 9"/>
 </svg>
 </button>
 {showBiometricGuide && (
 <div className="modern-login-biometric-guide-content">
 <div className="modern-login-guide-card">
 <div className="modern-login-guide-header">
 <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
 <path d="M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
 <path d="M12 4c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8"/>
 <path d="M12 2a10 10 0 0 1 10 10"/>
 </svg>
 <span>Đăng nhập siêu nhanh bằng Vân tay hoặc Face ID</span>
 </div>
 <div className="modern-login-guide-steps">
 <div className="modern-login-guide-step">
 <div className="modern-login-guide-step-num">1</div>
 <div className="modern-login-guide-step-text">
 <strong>Đăng nhập bình thường</strong> bằng tài khoản & mật khẩu
 </div>
 </div>
 <div className="modern-login-guide-step">
 <div className="modern-login-guide-step-num">2</div>
 <div className="modern-login-guide-step-text">
 Vào <strong>Hồ sơ cá nhân</strong> → tab <strong>"Bảo mật & Chấm công"</strong>
 </div>
 </div>
 <div className="modern-login-guide-step">
 <div className="modern-login-guide-step-num">3</div>
 <div className="modern-login-guide-step-text">
 Nhấn <strong>"Đăng ký Vân tay / Face ID"</strong> và quét vân tay
 </div>
 </div>
 <div className="modern-login-guide-step">
 <div className="modern-login-guide-step-num"></div>
 <div className="modern-login-guide-step-text">
 Từ lần sau, chỉ cần bấm nút bên trên là đăng nhập ngay!
 </div>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 )}

 <div className="modern-login-system-info">
 <p>Hệ thống quản lý New Atlantic</p>
 </div>
 </div>
 </div>
 </div>
 </>
 );
};

export default Login;
